<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useRoute } from "vue-router"
import { useSourceStore } from "@core/stores/source.store"
import { storeToRefs } from "pinia"
import { METER_TYPES_LIST } from "@core/constants/source"

// ================================
// PROPS & EMITS
// ================================

// ================================
// COMPOSABLES
// ================================
const sourceStore = useSourceStore()
const route = useRoute()

const { getSourceById } = storeToRefs(sourceStore)

// ================================
// COMPUTED
// ================================
/**
 * Identifiant de la source depuis les paramètres de route
 */
const getSourceId = computed<string | undefined>(() => {
    return route?.params?._id_source as string | undefined
})

/**
 * Source courante (depuis le store)
 */
const getSource = computed(() => {
    if (!getSourceId.value) return undefined
    return getSourceById.value(getSourceId.value)
})

/**
 * Type de source (meter, sensor ou meteo)
 * Un compteur a un type parmi : "pdl", "pce", "cpcu", "regaz", "elecsavoie", "dalkia"
 * Un capteur a le type : "iot"
 * Une station météo a le type : "meteo"
 */
const getSourceType = computed<"meter" | "sensor" | "meteo" | undefined>(() => {
    if (!getSource.value) return undefined

    const type = getSource.value.type

    // Types de compteurs
    if (METER_TYPES_LIST.includes(type)) {
        return "meter"
    }

    // Type de capteur
    if (type === "iot") {
        return "sensor"
    }

    // Type station météo
    if (type === "meteo") {
        return "meteo"
    }

    return undefined
})

/**
 * Indique si le chargement de la source affiche un skeleton (non silencieux).
 * True lorsque le store charge et que le type de la source n'est pas encore connu.
 */
const getIsNotFetchingSilent = computed(() => {
    return sourceStore.isFetchingSourceById && !getSource.value?.type
})
</script>

<template>
    <div class="page page-source-info-general">
        <!-- ================================ -->
        <!-- ÉTAT DE CHARGEMENT -->
        <!-- ================================ -->
        <BaseSkeleton
            v-if="getIsNotFetchingSilent"
            width="100%"
            height="90vh"
        />

        <!-- ================================ -->
        <!-- AFFICHAGE SELON LE TYPE DE SOURCE -->
        <!-- ================================ -->
        <MeterInfoWrapper
            v-else-if="getSourceType === 'meter' && getSourceId"
            :meter-id="getSourceId"
        />
        <SensorInfoWrapper
            v-else-if="getSourceType === 'sensor' && getSourceId"
            :source-id="getSourceId"
        />
        <WeatherStationInfoWrapper
            v-else-if="getSourceType === 'meteo' && getSourceId"
            :source-id="getSourceId"
        />
    </div>
</template>
